import React, { useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Label,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import Select from "react-select";
import * as Yup from "yup";

import {
  fetchCouponById,
  addCoupon,
  updateCoupon,
  fetchAllSubscriptionPlans,
} from "../../../slices/thunks";
import i18n from "../../../i18n";

export const CouponSchema = Yup.object().shape({
  Code: Yup.string()
    .required("Coupon code is required")
    .matches(/^[A-Z0-9_]+$/, "Use uppercase letters only"),

  Description: Yup.string().nullable(),

  DiscountType: Yup.string()
    .oneOf(["PERCENT", "FIXED"])
    .required("Discount type is required"),

  DiscountValue: Yup.number()
    .required("Discount value is required")
    .min(0, "Discount cannot be negative"),

  MaxDiscountAmount: Yup.number()
    .nullable()
    .min(0, "Amount cannot be negative"),

  ValidFrom: Yup.date().required("Valid from date is required"),

  ValidTo: Yup.date()
    .required("Valid to date is required")
    .min(Yup.ref("ValidFrom"), "Valid To must be after Valid From"),

  MaxTotalUsage: Yup.number()
    .nullable()
    .min(1, "Must be greater than 0"),

  MaxUsagePerUser: Yup.number()
    .nullable()
    .min(1, "Must be at least 1"),

  PlanIDs: Yup.array()
  .of(Yup.number())
  .min(1, "Select at least one plan") // must have at least 1
  .required("Select at least one plan"),
});

const emptyCoupon = {
  ID: null,
  Code: "",
  Description: "",
  DiscountType: "PERCENT",
  DiscountValue: 0,
  MaxDiscountAmount: "",
  ValidFrom: "",
  ValidTo: "",
  IsFirstTimeOnly: false,
  IsStudentOnly: false,
  MaxTotalUsage: "",
  MaxUsagePerUser: 1,
  PlanIDs: [],
};

function CouponEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { loading, selectedCoupon, plans } = useSelector((state) => ({
    loading: state.Coupon.loading,
    selectedCoupon: state.Coupon.selectedCoupon,
    plans: state.SubscriptionPlan.plans,
  }));

  /* ---------------- LOAD DATA ---------------- */
  useEffect(() => {
    if (isEdit) {
      dispatch(fetchCouponById({ ID: id }));
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    dispatch(fetchAllSubscriptionPlans());
  }, [])

  const initialValues = isEdit && selectedCoupon ? selectedCoupon : emptyCoupon;

  /* ---------------- UI ---------------- */
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={isEdit ? t("Edit Coupon") : t("Add Coupon")} />

        {loading ? (
          <div className="text-center">
            <Spinner />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={CouponSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setSubmitting(true);
                if (isEdit) {
                  await dispatch(updateCoupon(values)).unwrap();
                } else {
                  await dispatch(addCoupon(values)).unwrap();
                }
                navigate("/payment/coupons");
              } catch (err) {
                console.error(err);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              setFieldTouched,
              isSubmitting,
            }) => (
              <Form>
                {console.log('errors: ', errors)}
                <Row>
                  <Col md={12}>
                    {/* BASIC INFO */}
                    <Card className="mb-3">
                      <CardHeader>{t("Basic Information")}</CardHeader>
                      <CardBody>
                        <Row className="mb-3">
                          <Col md={6}>
                            <Input
                              name="Code"
                              placeholder={t("Coupon Code")}
                              value={values.Code}
                              disabled={isEdit}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.Code && !!errors.Code}
                            />
                            <div className="invalid-feedback">
                              {t(errors.Code)}
                            </div>
                          </Col>

                          <Col md={6}>
                            <Input
                              type="select"
                              name="DiscountType"
                              value={values.DiscountType}
                              onChange={handleChange}
                            >
                              <option value="PERCENT">{t("Percent")}</option>
                              <option value="FIXED">{t("Fixed")}</option>
                            </Input>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <Input
                              type="number"
                              name="DiscountValue"
                              placeholder={t("Discount Value")}
                              value={values.DiscountValue}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={
                                touched.DiscountValue &&
                                !!errors.DiscountValue
                              }
                            />
                            <div className="invalid-feedback">
                              {t(errors.DiscountValue)}
                            </div>
                          </Col>

                          <Col md={6}>
                            <Input
                              type="number"
                              name="MaxDiscountAmount"
                              placeholder={t("Max Discount Amount")}
                              value={values.MaxDiscountAmount}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <Label>{t("Valid From")}</Label>
                            <Input
                              type="datetime-local"
                              name="ValidFrom"
                              value={values.ValidFrom}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={
                                touched.ValidFrom && !!errors.ValidFrom
                              }
                            />
                            <div className="invalid-feedback">
                              {t(errors.ValidFrom)}
                            </div>
                          </Col>

                          <Col md={6}>
                            <Label>{t("Valid To")}</Label>
                            <Input
                              type="datetime-local"
                              name="ValidTo"
                              value={values.ValidTo}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.ValidTo && !!errors.ValidTo}
                            />
                            <div className="invalid-feedback">
                              {t(errors.ValidTo)}
                            </div>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <Input
                              type="number"
                              name="MaxTotalUsage"
                              placeholder={t("Max Total Usage")}
                              value={values.MaxTotalUsage}
                              onChange={handleChange}
                            />
                          </Col>

                          <Col md={6}>
                            <Input
                              type="number"
                              name="MaxUsagePerUser"
                              placeholder={t("Max Usage Per User")}
                              value={values.MaxUsagePerUser}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <Select
                              isMulti
                              name="PlanIDs"
                              isRtl={i18n.language === "ar"}
                              classNamePrefix="react-select"
                              className={touched.PlanIDs && errors.PlanIDs ? "form-control is-invalid p-0" : ""}
                              styles={{
                                multiValueLabel: (base) => ({
                                  ...base,
                                  color: "white"
                                }),
                              }}
                              placeholder={t("Select Plans")}
                              options={plans.map((p) => ({
                                value: p.ID,
                                label: i18n.language === "ar" ? p.NameAr : p.NameEn,
                              }))}
                              value={plans
                                .filter((p) => values.PlanIDs.includes(p.ID))
                                .map((p) => ({
                                  value: p.ID,
                                  label: i18n.language === "ar" ? p.NameAr : p.NameEn,
                                }))}
                                onChange={(selectedOptions) => {
                                  const planIds = selectedOptions
                                    ? selectedOptions.map((o) => o.value)
                                    : [];
                                  setFieldValue("PlanIDs", planIds); 
                                }}
                                onBlur={() => setFieldTouched("PlanIDs", true)}
                            />

                            {/* Validation error */}
                            {touched.PlanIDs && errors.PlanIDs && (
                              <div className="text-danger mt-1">
                                {t(errors.PlanIDs)}
                              </div>
                            )}
                          </Col>

                          <Col md={3}>
                            <Input
                              type="checkbox"
                              name="IsFirstTimeOnly"
                              className="me-2"
                              checked={values.IsFirstTimeOnly}
                              onChange={handleChange}
                            />{" "}
                            {t("First Time Only")}
                          </Col>

                          <Col md={3}>
                            <Input
                              type="checkbox"
                              name="IsStudentOnly"
                              className="me-2"
                              checked={values.IsStudentOnly}
                              onChange={handleChange}
                            />{" "}
                            {t("Student Only")}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* ACTIONS */}
                    <div className="d-flex text-end gap-2 mb-3">
                      <Button
                        color="secondary"
                        onClick={() => navigate(-1)}
                      >
                        {t("Cancel")}
                      </Button>
                      <Button
                        color="success"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {t("Save")}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        )}
      </Container>
    </div>
  );
}

export default CouponEditor;